/**
 * Review & publish (section 3, GOV.UK's "Check answers"): everything the next publish changes,
 * grouped Added / Updated / Removed, what must be fixed first, what is worth a look, and one button
 * that says exactly what it does. Nothing reaches the apps until it is pressed.
 */

import { useCallback, useEffect, useState } from "react";
import Button from "@web-ui/components/Button";
import Empty from "@web-ui/components/Empty";
import ListRow from "@web-ui/components/ListRow";
import Notice from "@web-ui/components/Notice";
import { publishing, type EntitySummary, type PendingChange, type Problem } from "@/api/admin";
import { ApiError } from "@/api/client";
import { PageBar } from "@/components/page-bar";
import { StatusBadge } from "@/components/status-badge";
import { href, itemRoute } from "@/lib/route";
import { groupChanges, kindWord, publishButtonLabel } from "@/publishing/changes";
import { plainProblem } from "@/publishing/problems";
import { useStudio } from "@/shell/studio";

const time = (ms: number) => new Date(ms).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

export function ReviewPublish() {
  const { client, pack, changes, items, refresh, canEdit } = useStudio();
  const [problems, setProblems] = useState<Problem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState<{ at: number; warnings: Problem[] } | null>(null);
  const [error, setError] = useState("");
  const brand = client.brand.id;

  const check = useCallback(async () => {
    try { setProblems((await publishing.validate(client)).problems); } catch { setProblems([]); setError("The check did not run. Reload the page to try again."); }
  }, [client]);
  useEffect(() => { void check(); }, [check]);

  const list = changes?.changes ?? [];
  const blockers = (problems ?? []).filter((p) => p.level === "error");
  const warnings = (problems ?? []).filter((p) => p.level === "warning");
  const groups = groupChanges(list);

  const publish = async () => {
    setBusy(true); setError("");
    try {
      const out = await publishing.publish(client);
      setPublished({ at: out.publish.publishedAt, warnings: out.warnings });
      await Promise.all([refresh(), check()]);
    } catch (e) {
      if (e instanceof ApiError && e.code === "feed_invalid") { setProblems(e.body["problems"] as Problem[]); setError("Not published: fix the problems marked “Must fix” first."); }
      else setError("The publish did not go through. Nothing changed in the apps. Try again.");
    } finally { setBusy(false); }
  };

  /** The item a problem is about, when it names one the editor knows. */
  const itemOf = (p: Problem): EntitySummary | undefined => (p.itemId ? items?.find((e) => e.id === p.itemId) : undefined);

  return (
    <>
      <PageBar crumbs={[{ label: "Home", href: href({ brand, section: "home" }) }, { label: "Review & publish", href: href({ brand, section: "review" }) }]} />

      {published ? (
        <Notice tone="positive" title={`Published at ${time(published.at)}`} action={<Button as="a" size="sm" variant="secondary" href={href({ brand, section: "settings", page: "history" })}>See history</Button>}>
          Live in the apps within a minute.{published.warnings.length ? ` ${published.warnings.length} ${published.warnings.length === 1 ? "thing is" : "things are"} still worth checking below.` : ""}
        </Notice>
      ) : null}
      {error ? <Notice tone="critical" onDismiss={() => setError("")}>{error}</Notice> : null}
      {changes && !changes.known ? (
        <Notice tone="warning" title="What will change can't be shown yet">The version in the app was published before the console kept track of changes. Your next publish starts tracking them.</Notice>
      ) : null}

      {blockers.length ? (
        <section className="card flex flex-col gap-3" aria-labelledby="must-fix">
          <h2 id="must-fix" className="text-sub-title-1 ps-card-inset">Must fix before publishing <span className="text-t-tertiary">({blockers.length})</span></h2>
          <ProblemRows problems={blockers} tone="critical" itemOf={itemOf} pack={pack} />
        </section>
      ) : null}

      {!changes ? (
        <section className="card" aria-busy="true"><p className="p-4 text-body-2 text-t-secondary">Checking what has changed…</p></section>
      ) : list.length === 0 ? (
        <section className="card">
          <Empty icon="check-circle" title="All changes are live" description="Everything saved in the console is in the apps. Edits you save from now on appear here before they go out." />
        </section>
      ) : (
        <>
          <ChangeGroup title="Added" note="New in the app after this publish." changes={groups.added} />
          <ChangeGroup title="Updated" note="In the app now; your saved changes go out." changes={groups.updated} />
          <ChangeGroup title="Removed" note="Taken out of the app. They stay in the console." changes={groups.removed} />
        </>
      )}

      {warnings.length ? (
        <section className="card flex flex-col gap-3" aria-labelledby="worth-checking">
          <div className="ps-card-inset">
            <h2 id="worth-checking" className="text-sub-title-1">Worth checking <span className="text-t-tertiary">({warnings.length})</span></h2>
            <p className="text-body-2 text-t-secondary">These don't stop a publish, but viewers may notice them.</p>
          </div>
          <ProblemRows problems={warnings} tone="warning" itemOf={itemOf} pack={pack} />
        </section>
      ) : null}

      <section className="card flex flex-col gap-3">
        <div className="ps-card-inset">
          <h2 className="text-sub-title-1">Publish</h2>
          <p className="text-body-2 text-t-secondary">
            {problems === null ? "Checking your items…"
              : blockers.length ? `Fix the ${blockers.length === 1 ? "problem" : `${blockers.length} problems`} above first. Nothing goes to the apps until you publish.`
              : list.length ? "Nothing goes to the apps until you publish. The apps pick it up within a minute."
              : "There is nothing new to send. Changed the brand's colours or menu? A publish sends those too."}
          </p>
        </div>
        {canEdit ? (
          <div className="flex flex-wrap gap-2">
            {list.length > 0 || problems === null || blockers.length ? <Button variant="primary" type="button" isPending={busy} disabled={busy || problems === null || blockers.length > 0 || list.length === 0} onClick={() => void publish()}>{publishButtonLabel(list.length)}</Button> : null}
            {list.length === 0 && problems !== null && !blockers.length ? <Button variant="outline" type="button" disabled={busy} onClick={() => void publish()}>Publish anyway</Button> : null}
          </div>
        ) : <p className="text-body-2 text-t-secondary ps-card-inset">Editors and owners can publish. You can look.</p>}
      </section>
    </>
  );
}

function ChangeGroup({ title, note, changes: rows }: { title: string; note: string; changes: PendingChange[] }) {
    const { client, pack, stateOf } = useStudio();
    const brand = client.brand.id;
    if (!rows.length) return null;
    return (
      <section className="card flex flex-col gap-3" aria-label={`${title}: ${rows.length}`}>
        <div className="ps-card-inset">
          <h2 className="text-sub-title-1">{title} <span className="text-t-tertiary">({rows.length})</span></h2>
          <p className="text-body-2 text-t-secondary">{note}</p>
        </div>
        <div className="flex flex-col">
          {rows.map((c) => {
            const exists = !!stateOf(c.type, c.id);
            return (
              <ListRow key={`${c.type}:${c.id}`} title={c.label || c.id} detail={kindWord(pack, c.type)}
                href={exists ? href(itemRoute(brand, c.type, c.id)) : undefined}
                meta={exists ? <StatusBadge state={stateOf(c.type, c.id)} /> : <span className="text-caption text-t-tertiary">Deleted</span>} />
            );
          })}
        </div>
      </section>
    );
}

function ProblemRows({ problems, tone, itemOf, pack }: { problems: Problem[]; tone: "critical" | "warning"; itemOf: (p: Problem) => EntitySummary | undefined; pack: ReturnType<typeof useStudio>["pack"] }) {
  const { client, items } = useStudio();
  const nameOf = (id: string) => items?.find((e) => e.id === id)?.label || undefined;
  return (
    <div className="flex flex-col">
      {problems.map((p, i) => {
        const item = itemOf(p);
        return (
          <ListRow key={i} icon={tone === "critical" ? "close-circle" : "warning"} tone={tone}
            title={plainProblem(p.message, pack, nameOf)}
            detail={item ? `${kindWord(pack, item.type)} “${item.label || item.id}”` : p.path === "brand.menu" ? "The app's menu (Settings → Brand)" : p.itemId ? `Item ${p.itemId}` : undefined}
            href={item ? href({ ...itemRoute(client.brand.id, item.type, item.id), ...(p.field ? { field: p.field } : {}) }) : p.path === "brand.menu" ? href({ brand: client.brand.id, section: "settings", page: "brand" }) : undefined} />
        );
      })}
    </div>
  );
}
